import type { SearchSuggestion } from '~/types'

interface SuggestionDto {
  suggestions: Array<SearchSuggestion>
}

export const useSearchStore = defineStore('search', () => {
  const data = ref<SuggestionDto>({ suggestions: [] })
  const history = ref<SuggestionDto>({ suggestions: [] })
  const query = ref<string | null>(null)
  const isLoading = ref(false)

  function resetToHistory() {
    data.value.suggestions = history.value.suggestions
  }

  async function getSearchHistory() {
    const { baseURL } = useRuntimeConfig().app
    const { $i18n, $storeErrorLog } = useNuxtApp()
    isLoading.value = true

    try {
      const { data: responseData, error } = await useFetch<SuggestionDto>('/api/searchHistory', { baseURL })
      if (error.value)
        throw error.value
      if (responseData.value == null)
        throw new Error('Failed to load search-history')

      data.value = responseData.value
      query.value = null
      isLoading.value = false
      history.value = responseData.value
    }
    catch (err: unknown) {
      $storeErrorLog('stores/search.ts', 'Could not load search history', $i18n.t('errors.page.unknown.subtitle'), err)
    }
  }

  async function autocomplete(searchQuery: string) {
    const { baseURL } = useRuntimeConfig().app
    const { $i18n, $storeErrorLog } = useNuxtApp()

    isLoading.value = true
    try {
      const { data: responseData, error } = await useFetch<SuggestionDto>(`/api/v2/findSuggestions?q=${searchQuery}`, {
        baseURL,
        onResponseError({ response }) {
          if (response.status === 401) useUserStore().isAuthenticated = false
        },
      })
      if (error.value)
        throw error.value
      if (responseData.value == null)
        throw new Error('Failed to find suggestions')

      data.value.suggestions = responseData.value.suggestions
      query.value = searchQuery
    }
    catch (err: unknown) {
      let snackbarMessage = $i18n.t('errors.page.unknown.subtitle')
      if (err && typeof err === 'object' && 'data' in err) {
        const errorData = err as { data?: { data?: string } }
        if (errorData.data?.data)
          snackbarMessage = errorData.data.data
      }
      $storeErrorLog('stores/search.ts', `Could not load autocomplete by query: ${searchQuery}`, snackbarMessage, err)
    }

    isLoading.value = false
  }

  function reset() {
    data.value = { suggestions: [] }
    history.value = { suggestions: [] }
    query.value = null
    isLoading.value = false
  }

  return {
    data,
    history,
    query,
    isLoading,
    resetToHistory,
    getSearchHistory,
    autocomplete,
    reset,
  }
})
